import { useState } from "react";

import XClose from "@uui/XClose";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Box, IconButton, Typography } from "@mui/material";

import { getPlanPriceByPlanAndCadence } from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import UpsellEnterprise from "@app/analytics/MeetingReport/assets/upsell_enterprise.png";
import UpsellPro from "@app/analytics/MeetingReport/assets/upsell_pro.png";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import { BillingCadence, BillingPlan } from "@app/common/types";
import { withoutQueryParams } from "@app/common/utils";
import { formatCurrency } from "@app/utils/utils";

import Button from "@rui/Button";
import { Link } from "@rui/Link";
import Modal from "@rui/Modal";
import Stack, { Column, Row } from "@rui/Stack";
import Switch from "@rui/Switch";

import {
  Chip,
  DisclaimerLink,
  Feature,
  PlanDescription,
  PlanDetails,
  PlanName,
  PlanRadio,
  Pricing,
  SwitchLabel,
} from "./styles";

export interface UpsellModalWrapperProps {
  currentPlan: BillingPlan;
}

interface UpsellModalProps extends UpsellModalWrapperProps {
  open: boolean;
  onClose: () => void;
  useLegacyPrices: boolean;
}

const CADENCE_LABEL = {
  annual: "annually",
  monthly: "monthly",
};

export const UpsellModal = ({
  currentPlan,
  onClose,
  open,
  useLegacyPrices,
}: UpsellModalProps) => {
  const [selectedCadence, setSelectedCadence] =
    useState<BillingCadence>("annual");
  const [selectedPlan, setSelectedPlan] = useState<BillingPlan>("enterprise");

  const showAnnualPrices = selectedCadence === "annual";
  const toggleCadence = () => {
    setSelectedCadence(selectedCadence === "annual" ? "monthly" : "annual");
  };

  const onProPlan = currentPlan === "pro";

  const selectPlan = (plan: BillingPlan) => {
    if (onProPlan) return;
    return () => setSelectedPlan(plan);
  };

  const monthlySeatPrice = getPlanPriceByPlanAndCadence({
    plan: selectedPlan,
    cadence: selectedCadence,
    useLegacyPrices: useLegacyPrices,
  });

  return (
    <Modal
      open={open}
      sx={{ minWidth: { xxs: "100%", md: "600px" } }}
      onClose={onClose}
    >
      <IconButton
        sx={{ position: "absolute", top: 16, right: 16 }}
        size="small"
        onClick={onClose}
      >
        <XClose />
      </IconButton>
      <Column variant="center" sx={{ p: 2, maxWidth: "100%" }} spacing={2}>
        <Column variant="center" spacing={1}>
          <Typography
            variant="h1"
            sx={{ color: "shades.neutral.800", textAlign: "center" }}
          >
            Stop taking meeting notes
          </Typography>
          <Typography
            variant="bodySecondaryReg"
            sx={{ color: "shades.neutral.500", textAlign: "center" }}
          >
            {onProPlan
              ? "Upgrade to Read Enterprise for video playback and highlight reels"
              : "Get premium features with Read Pro or Enterprise"}
          </Typography>
        </Column>
        <Row
          variant="center"
          onClick={toggleCadence}
          sx={{ cursor: "pointer" }}
        >
          <SwitchLabel>Monthly</SwitchLabel>
          <Switch checkedColor="#4C47CC" checked={showAnnualPrices} />
          <SwitchLabel selected={showAnnualPrices}>Annually</SwitchLabel>
        </Row>
        <Stack
          direction={{ xxs: "column", md: "row" }}
          sx={{ width: "100%" }}
          spacing={2}
        >
          <PlanDetails
            selected={selectedPlan === "pro"}
            onClick={selectPlan("pro")}
          >
            <Row variant="apart">
              <PlanName>Pro</PlanName>
              {onProPlan ? (
                <Chip
                  label="Current Plan"
                  sx={{
                    color: "shades.meadow.500",
                    bgcolor: "shades.meadow.50",
                  }}
                />
              ) : (
                <PlanRadio checked={selectedPlan === "pro"} />
              )}
            </Row>
            <PlanDescription>Basic note-taking</PlanDescription>
            <Row variant="apart">
              <Column spacing="2px" sx={{ pt: 0.5 }}>
                <Feature supported>Meeting notes</Feature>
                <Feature supported>Transcript</Feature>
                <Feature supported>Highlights</Feature>
                <Feature>Video playback</Feature>
              </Column>
              <Box component="img" src={UpsellPro} sx={{ width: "90px" }} />
            </Row>
          </PlanDetails>
          <PlanDetails
            selected={selectedPlan === "enterprise"}
            onClick={selectPlan("enterprise")}
          >
            <Row variant="apart">
              <Row spacing={1}>
                <PlanName>Enterprise</PlanName>
                <Chip
                  label="Recommended"
                  sx={{
                    color: "chart.teal.dark",
                    bgcolor: "#E3F9FD",
                    display: { xxs: "none", xs: "flex" },
                  }}
                />
              </Row>
              <PlanRadio checked={selectedPlan === "enterprise"} />
            </Row>
            <PlanDescription>Advanced note-taking</PlanDescription>
            <Row variant="apart">
              <Column spacing="2px" sx={{ pt: 0.5 }}>
                <Feature supported>Meeting notes</Feature>
                <Feature supported>Transcript</Feature>
                <Feature supported>Highlights</Feature>
                <Feature supported>Video playback</Feature>
              </Column>
              <Box
                component="img"
                src={UpsellEnterprise}
                sx={{ width: "90px" }}
              />
            </Row>
          </PlanDetails>
        </Stack>
        <Row variant="center">
          <DisclaimerLink href={LINKS.MARKETING.PLANS_PRICING}>
            Compare plans
          </DisclaimerLink>
        </Row>
        <Pricing
          direction={{ xxs: "column", sm: "row" }}
          spacing={2}
          variant="apart"
        >
          <Column alignItems={{ xxs: "center", sm: "flex-start" }}>
            <Typography variant="h1" sx={{ color: "shades.neutral.800" }}>
              {formatCurrency(monthlySeatPrice)} per month
            </Typography>
            <Typography
              variant="bodySecondaryReg"
              sx={{ color: "shades.neutral.500" }}
            >
              billed {CADENCE_LABEL[selectedCadence]}
            </Typography>
          </Column>
          <Link
            href={ROUTES.UPGRADE_ACCOUNT}
            sx={{ width: { xxs: "100%", sm: "auto" } }}
          >
            <Button sx={{ minWidth: { sm: "216px" }, width: "100%" }}>
              Upgrade
            </Button>
          </Link>
        </Pricing>
      </Column>
    </Modal>
  );
};

const UpsellModalWrapper = (props: UpsellModalWrapperProps) => {
  const [queryParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isActivePopup = queryParams.get("popup") === "upgrade";
  const { isEligibleForLegacyPricing } = useBillingContext();

  const handleClose = () => {
    navigate(withoutQueryParams(location.pathname, location.search, ["popup"]));
  };

  return (
    <UpsellModal
      open={isActivePopup}
      onClose={handleClose}
      {...props}
      useLegacyPrices={isEligibleForLegacyPricing}
    />
  );
};

export default UpsellModalWrapper;
